import { Children } from 'react';
import { Tooltip as BSTooltip, OverlayTrigger } from 'react-bootstrap';
import type { CSSProperties, FunctionComponent, ReactElement, ReactNode } from 'react';

import styles from './Tooltip.module.less';

export type TooltipPlacement = 'top' | 'bottom' | 'right' | 'left';

export type TooltipProps = {
  /**
   * Unique id for the tooltip. This helps with a11y.
   */
  id: string;
  /**
   * Tooltip text. Can also be a ReactNode.
   */
  tooltip: string | ReactNode;
  /**
   * Location of tooltip relative to the button
   */
  placement?: TooltipPlacement;
  /**
   * If the tooltip should be displayed or not (children rendered as fallback).
   */
  showTooltip?: boolean;
  /**
   * Callback fired after the tooltip finishes transitioning out.
   */
  onExited?: () => void;
};

/**
 * Finally, an uncomplicated Tooltip component for the masses!
 * No more importing two different Bootstrap components, just an anchor + tooltip!
 *
 * This component will pass down to its child some event handler props — onMouseOver, onMouseOut, onFocus, and onBlur at
 * the time of writing. If the child is a React component you need to ensure those props will reach some DOM element
 * rendered by the component (see the example below). If the child is DOM element you don't have to worry about it.
 *
 * @param {TooltipProps} props - props object
 * @param {string} props.id - Unique id for the tooltip. This helps with a11y.
 * @param {ReactNode} props.children - A single element or component to be "attached" to. It should have `display: inline-block`.
 * @param {string | ReactNode} props.tooltip - Tooltip text. Can also be a ReactNode.
 * @param {'top' | 'bottom' | 'right' | 'left'} [props.placement] - Placement of the tooltip.
 * @param {boolean} [props.showTooltip] - If the tooltip should be displayed or not (children rendered as fallback).
 * @example
 * <Tooltip tooltip="Some more info" placement="right">
 *   <span>hover me</span>
 * </Tooltip>
 * @example
 * const Component = (props) => <span {...props}>hover me</span>
 * <Tooltip tooltip="Some more info" placement="right">
 *   <Component>hover me</Component>
 * </Tooltip>
 */
const Tooltip: FunctionComponent<TooltipProps> = ({
  id,
  children,
  tooltip,
  placement = 'top',
  showTooltip = true,
  onExited,
}) =>
  showTooltip && tooltip ? (
    <OverlayTrigger
      placement={placement}
      overlay={
        <BSTooltip id={id} className={styles.tooltip}>
          {tooltip}
        </BSTooltip>
      }
      onExited={onExited}
    >
      {Children.only(children)}
    </OverlayTrigger>
  ) : (
    (children as ReactElement)
  );

type TooltipContentProps = {
  maxWidth?: CSSProperties['maxWidth'];
};

/**
 * A styled tooltip content component with padding, primary font, and medium font weight that optionally limits content max width.
 */
export const TooltipContent: FunctionComponent<TooltipContentProps> = ({ children, maxWidth }) => {
  return (
    <div className={styles.tooltipContent} style={{ maxWidth }}>
      {children}
    </div>
  );
};

export default Tooltip;
